/**
 * The till.
 *
 * The most used screen in the shop, so it is the fastest: scan and the item is
 * on the sale, no clicking into a box first. Everything else is one tap away.
 *
 * It works with the internet down. Items come from the device's own cache, the
 * receipt number comes from this till's own counter, and the finished sale is
 * written to the device before anything is sent anywhere.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  applyPromotions,
  compareSizes,
  computeSale,
  newId,
  NO_DISCOUNT,
  saleDiscountBase,
  type CatalogItem,
  type Discount,
  type SaleLineInput,
} from '@pos/shared';
import { useI18n, usePlural, useT } from '../../core/i18n/index.js';
import { useSettings } from '../../core/state/settings.js';
import { useAuth } from '../../core/state/auth.js';
import { useSync } from '../../core/sync.js';
import { useToast } from '../../core/state/toast.js';
import { cacheCompletedSale, db, findByBarcode, searchItems } from '../../core/db.js';
import { listenForScanner } from '../../core/scanner.js';
import { beepDone, beepOk, beepUnknown, beepWarn } from '../../core/sound.js';
import { deviceId, takeReceiptNumber } from '../../core/device.js';
import { api } from '../../core/api.js';
import { shopKey } from '../../core/shop.js';
import { printNoSale, printReceipt, type ReceiptData } from '../../core/print.js';
import { HelperError, helperProblem, notifyPrintProblem, openDrawerViaHelper, printRoute } from '../../core/printing.js';
import { Button, Confirm, Field, Modal, MoneyInput, Pill, Swatch } from '../../core/ui/ui.js';
import { CameraButton, Money, useApproval } from '../../core/ui/bits.js';
import { PaymentDialog, type TakenPayment } from './Payment.js';
import { useRunningPromotions } from '../../core/promotions.js';
import { ProductGrid } from './ProductGrid.js';

interface Line {
  id: string;
  item: CatalogItem;
  qty: number;
  discount: Discount;
  addedAt: number;
}

/** What was stored when a sale was parked. */
interface HeldPayload {
  /*
   * `item` is a copy of the item as it was when the sale was parked. The cached
   * catalogue is still preferred when the sale comes back - a price may have
   * been corrected in between - but carrying the copy means a parked sale can
   * always be picked up, even on a device whose cache is still filling.
   */
  lines: { variantId: string; qty: number; discount?: Discount; item?: CatalogItem }[];
  saleDiscount?: Discount;
}

/**
 * The sale in progress, kept on the device for each cashier.
 *
 * Without it a sale lived only on this screen: one tap on Price check, a
 * refresh, or the app updating itself, and every item rung up was gone with
 * the customer still standing there. Kept per cashier, so whoever signs in
 * next does not find somebody else's half-finished sale.
 */
interface KeptSale {
  lines: { id: string; item: CatalogItem; qty: number; discount?: Discount }[];
  saleDiscount?: Discount;
  customer?: { id: string; name: string; phone?: string } | null;
  approvals?: { reason: string; managerId: string }[];
  approvalToken?: string | null;
}

const keptKey = (userId: string | undefined) => shopKey(`pos.sale.${userId ?? 'anyone'}`);

function readKept(userId: string | undefined): KeptSale | null {
  try {
    const raw = localStorage.getItem(keptKey(userId));
    return raw ? (JSON.parse(raw) as KeptSale) : null;
  } catch {
    return null;
  }
}

export function Checkout() {
  const t = useT();
  const plural = usePlural();
  const { lang } = useI18n();
  const { settings, money } = useSettings();
  const { user, can, hasFeature } = useAuth();
  const { submitSale, state } = useSync();
  const toast = useToast();
  const approval = useApproval();

  const [kept] = useState(() => readKept(user?.id));
  const [lines, setLines] = useState<Line[]>(() =>
    (kept?.lines ?? []).map((l) => ({ id: l.id, item: l.item, qty: l.qty, discount: l.discount ?? NO_DISCOUNT, addedAt: 0 })),
  );
  const [saleDiscount, setSaleDiscount] = useState<Discount>(kept?.saleDiscount ?? NO_DISCOUNT);
  const [approvals, setApprovals] = useState<{ reason: string; managerId: string }[]>(kept?.approvals ?? []);
  // The signed proof behind an over-limit discount; the server checks it.
  const [approvalToken, setApprovalToken] = useState<string | null>(kept?.approvalToken ?? null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CatalogItem[]>([]);
  const [highlight, setHighlight] = useState(0);
  const [editing, setEditing] = useState<Line | null>(null);
  const [discountTarget, setDiscountTarget] = useState<'sale' | Line | null>(null);
  const [paying, setPaying] = useState(false);
  // On a phone the sale lives in a bar at the bottom; this opens it out.
  const [cartOpen, setCartOpen] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [unknown, setUnknown] = useState<string | null>(null);
  /* An item whose price is typed at the till, waiting for that price. */
  const [pricing, setPricing] = useState<{ item: CatalogItem; qty: number } | null>(null);
  const [done, setDone] = useState<{ receipt: ReceiptData; sent: boolean } | null>(null);
  const [shift, setShift] = useState<{ id: string } | null>(null);
  const [customer, setCustomer] = useState<{ id: string; name: string; phone?: string } | null>(kept?.customer ?? null);
  const [pickingCustomer, setPickingCustomer] = useState(false);
  /* Set when the customer is being named only so the sale can go on their slate. */
  const [payAfterNaming, setPayAfterNaming] = useState(false);

  const searchRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const resumed = useRef(false);
  /** Mirrors `lines` so callbacks can read them without a stale closure. */
  const linesRef = useRef<Line[]>([]);

  // --- the shift ------------------------------------------------------------
  useEffect(() => {
    if (!settings.shiftsEnabled) return;
    void (async () => {
      try {
        const s = await api<{ id: string } | null>('/sales/shifts/open');
        setShift(s);
        if (s) localStorage.setItem(shopKey('pos.shift'), JSON.stringify(s));
      } catch {
        const cached = localStorage.getItem(shopKey('pos.shift'));
        if (cached) setShift(JSON.parse(cached) as { id: string });
      }
    })();
  }, [settings.shiftsEnabled]);

  useEffect(() => {
    linesRef.current = lines;
  }, [lines]);

  // Keep the sale in progress on the device, and forget it once it is paid or cancelled.
  useEffect(() => {
    try {
      if (lines.length === 0 && !customer) localStorage.removeItem(keptKey(user?.id));
      else {
        const sale: KeptSale = {
          lines: lines.map((l) => ({ id: l.id, item: l.item, qty: l.qty, discount: l.discount })),
          saleDiscount,
          customer,
          approvals,
          approvalToken,
        };
        localStorage.setItem(keptKey(user?.id), JSON.stringify(sale));
      }
    } catch {
      /* storage full or blocked: the sale still works, it just is not kept */
    }
  }, [lines, saleDiscount, customer, approvals, approvalToken, user?.id]);

  // A kept sale comes back with today's copy of each item, in case a price was corrected since.
  useEffect(() => {
    if (!kept?.lines.length) return;
    void (async () => {
      const fresh = new Map<string, CatalogItem>();
      for (const l of kept.lines) {
        const item = await db.items.get(l.item.variantId).catch(() => undefined);
        if (item) fresh.set(l.item.variantId, item);
      }
      if (fresh.size) setLines((cur) => cur.map((l) => ({ ...l, item: fresh.get(l.item.variantId) ?? l.item })));
    })();
  }, [kept]);

  // --- picking a parked sale back up ---------------------------------------
  useEffect(() => {
    const held = (location.state as { resumeHeld?: { id: string; payload: unknown } } | null)?.resumeHeld;
    if (!held || resumed.current) return;
    resumed.current = true;

    const line = (item: CatalogItem, l: { qty: number; discount?: Discount }): Line => ({
      id: newId(),
      item,
      qty: l.qty,
      discount: l.discount ?? NO_DISCOUNT,
      addedAt: 0,
    });

    void (async () => {
      const payload =
        typeof held.payload === 'string'
          ? (JSON.parse(held.payload) as HeldPayload)
          : (held.payload as HeldPayload);
      const parked = payload.lines ?? [];

      if (parked.length > 0 && parked.every((l) => l.item)) {
        /*
         * Straight back on the till, from the copy parked with the sale.
         *
         * Reading the catalogue cache first looks harmless but is not: the cache
         * rewrites itself whenever the catalogue refreshes, and a read landing
         * in the middle of that waits for the whole rewrite - seconds, on a
         * tablet - with the cashier staring at an empty till. So put the sale up
         * first and ask the cache afterwards.
         */
        // A sale already on the till is added to, never silently replaced.
        const busy = linesRef.current.length > 0;
        setLines((cur) => [...cur, ...parked.map((l) => line(l.item as CatalogItem, l))]);
        if (!busy) setSaleDiscount(payload.saleDiscount ?? NO_DISCOUNT);
        else toast.warn(t('checkout.heldAddedTo'));

        // It is on the till now, so it must not also sit in the held list.
        await api(`/sales/held/${held.id}`, { method: 'DELETE' }).catch(() => undefined);
        navigate('/checkout', { replace: true, state: null });

        // Then today's copy of each item, in case a price was corrected since.
        const fresh = new Map<string, CatalogItem>();
        for (const l of parked) {
          const item = await db.items.get(l.variantId);
          if (item) fresh.set(l.variantId, item);
        }
        setLines((cur) =>
          cur.map((l) => {
            const today = fresh.get(l.item.variantId);
            return today ? { ...l, item: today } : l;
          }),
        );
        return;
      }

      {
        // Parked by an older version, which stored only the ids.
        const restored: Line[] = [];
        for (const l of parked) {
          const item = await db.items.get(l.variantId);
          if (item) restored.push(line(item, l));
        }
        if (restored.length < parked.length) toast.warn(t('checkout.someItemsGone'));
        const busy = linesRef.current.length > 0;
        setLines((cur) => [...cur, ...restored]);
        if (!busy) setSaleDiscount(payload.saleDiscount ?? NO_DISCOUNT);

        await api(`/sales/held/${held.id}`, { method: 'DELETE' }).catch(() => undefined);
        // Clear the router state so a reload does not put it back a second time.
        navigate('/checkout', { replace: true, state: null });
      }
    })();
  }, [location.state, navigate, t, toast]);

  // --- totals ---------------------------------------------------------------
  // Promotions apply by themselves, the same way the server will work them out.
  const promos = useRunningPromotions();
  const promoById = useMemo(() => new Map(promos.map((p) => [p.id, p])), [promos]);
  const promoHits = useMemo(
    () =>
      applyPromotions(
        lines.map((l) => ({
          id: l.id,
          productId: l.item.productId,
          categoryId: l.item.categoryId ?? null,
          brandId: l.item.brandId ?? null,
          qty: l.qty,
          unitPrice: l.item.price,
          openPrice: l.item.openPrice,
        })),
        promos,
      ),
    [lines, promos],
  );

  const calcLines: SaleLineInput[] = useMemo(
    () =>
      lines.map((l) => ({
        id: l.id,
        variantId: l.item.variantId,
        qty: l.qty,
        unitPrice: l.item.price,
        unitCost: l.item.avgCost,
        discount: l.discount,
        promoId: promoHits.get(l.id)?.promoId ?? null,
        promoDiscount: promoHits.get(l.id)?.amount ?? 0,
        finalSale: l.item.finalSale,
      })),
    [lines, promoHits],
  );

  /** The promotion on a line, for the screen and the receipt, when it took something off. */
  const promoOn = (lineId: string) => {
    const hit = promoHits.get(lineId);
    const p = hit ? promoById.get(hit.promoId) : undefined;
    return hit && p && hit.amount > 0 ? { nameEn: p.nameEn, nameAr: p.nameAr, amount: hit.amount } : null;
  };

  const totals = useMemo(
    () => computeSale(calcLines, saleDiscount, settings.vatRate),
    [calcLines, saleDiscount, settings.vatRate],
  );

  // --- adding items ---------------------------------------------------------

  const addItem = useCallback(
    (item: CatalogItem, qty = 1) => {
      /*
       * An item priced at the till has no price yet, so it cannot go straight
       * onto the sale: ask what it is worth first. Each one is its own line,
       * because two miscellaneous things at two different prices are two
       * different things however alike they look to the catalogue.
       */
      if (item.openPrice) {
        beepOk();
        setPricing({ item, qty });
        setQuery('');
        setResults([]);
        return;
      }

      // Work out the warning BEFORE touching state. A state updater runs during
      // React's render pass, and showing a toast from inside one updates another
      // component mid-render - which React rightly complains about.
      const already = linesRef.current.find((l) => l.item.variantId === item.variantId)?.qty ?? 0;
      if (item.stock <= 0) {
        beepWarn();
        toast.warn(t('checkout.noStock'));
      } else if (already + qty > item.stock) {
        beepWarn();
        toast.warn(t('checkout.outOfStock', { n: item.stock }));
      } else {
        beepOk();
      }

      setLines((current) => {
        const existing = current.find((l) => l.item.variantId === item.variantId);
        if (existing) {
          return current.map((l) =>
            l.id === existing.id ? { ...l, qty: l.qty + qty, addedAt: Date.now() } : l,
          );
        }
        return [...current, { id: newId(), item, qty, discount: NO_DISCOUNT, addedAt: Date.now() }];
      });
      setQuery('');
      setResults([]);
    },
    [t, toast],
  );

  /**
   * The cashier has said what it is worth.
   *
   * The price is written onto this line's own copy of the item, which is what
   * every figure on the screen and the line sent to the server already read -
   * so the typed price flows through the totals, the receipt and the sale
   * without any of them needing to know it was typed.
   */
  const addPriced = (item: CatalogItem, qty: number, price: number) => {
    setLines((current) => [
      ...current,
      { id: newId(), item: { ...item, price }, qty, discount: NO_DISCOUNT, addedAt: Date.now() },
    ]);
    setPricing(null);
    beepDone();
  };

  const handleScan = useCallback(
    async (code: string) => {
      const item = await findByBarcode(code);
      if (item) {
        addItem(item);
        return;
      }
      // Not in the cache: it may be brand new. Ask the server if we can.
      try {
        const fromServer = await api<CatalogItem>(`/catalog/barcode/${encodeURIComponent(code)}`);
        addItem(fromServer);
      } catch {
        beepUnknown();
        setUnknown(code);
      }
    },
    [addItem],
  );

  // A scanner types very fast and presses Enter; catch it anywhere on the page.
  useEffect(() => {
    return listenForScanner({
      onScan: (code) => void handleScan(code),
      enabled: () => !paying && !done && !editing && !discountTarget,
    });
  }, [handleScan, paying, done, editing, discountTarget]);

  // --- search ---------------------------------------------------------------
  useEffect(() => {
    let alive = true;
    const timer = setTimeout(async () => {
      if (query.trim().length < 2) {
        setResults([]);
        return;
      }
      const found = await searchItems(query, 25);
      if (alive) {
        setResults(found);
        setHighlight(0);
      }
    }, 140);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [query]);

  // --- keyboard shortcuts ---------------------------------------------------
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (done || paying) return;
      if (e.key === 'F2' && lines.length) {
        e.preventDefault();
        setPaying(true);
      } else if (e.key === 'F3') {
        e.preventDefault();
        searchRef.current?.focus();
      } else if (e.key === 'F4' && lines.length && settings.holdSalesEnabled) {
        e.preventDefault();
        void hold();
      } else if (e.key === 'F8') {
        e.preventDefault();
        // One stray key must not throw a whole sale away: ask, like the button does.
        if (lines.length) setConfirmCancel(true);
        else reset();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // --- actions --------------------------------------------------------------

  const reset = () => {
    setLines([]);
    setSaleDiscount(NO_DISCOUNT);
    setApprovals([]);
    setApprovalToken(null);
    setQuery('');
    setResults([]);
    setCustomer(null);
  };

  const setQty = (id: string, qty: number) => {
    if (qty <= 0) {
      // One tap too many on "−" takes the item off: say which, and offer it back.
      const at = linesRef.current.findIndex((x) => x.id === id);
      const gone = linesRef.current[at];
      setLines((l) => l.filter((x) => x.id !== id));
      if (gone) {
        const name = lang === 'ar' ? gone.item.nameAr || gone.item.nameEn : gone.item.nameEn || gone.item.nameAr;
        toast.undo(t('checkout.removed', { name }), t('checkout.undo'), () =>
          setLines((l) => (l.some((x) => x.id === gone.id) ? l : [...l.slice(0, at), gone, ...l.slice(at)])),
        );
      }
      return;
    }
    setLines((l) => l.map((x) => (x.id === id ? { ...x, qty } : x)));
  };

  /** A line discount is on what is left after its promotion - and so is the cashier's limit. */
  const lineBase = (l: Line) => l.item.price * l.qty - (promoHits.get(l.id)?.amount ?? 0);

  /** A discount above the cashier's limit needs a manager standing there. */
  const applyDiscount = async (target: 'sale' | Line, discount: Discount) => {
    const base = target === 'sale' ? saleDiscountBase(totals) : lineBase(target);
    const asPercent =
      discount.type === 'percent' ? discount.value : base > 0 ? (discount.value / base) * 100 : 0;
    const limit = user?.maxDiscountPercent ?? settings.maxCashierDiscountPercent;

    if (asPercent > limit) {
      const approved = await approval.request(
        ['till.discount.over_limit'],
        'over_discount_limit',
        t('approval.reasonOverDiscount', { limit }),
      );
      if (!approved) return;
      setApprovals((a) => [...a, { reason: 'over_discount_limit', managerId: approved.managerId }]);
      setApprovalToken(approved.approvalToken);
      discount = { ...discount, approvedByUserId: approved.managerId };
    }

    if (target === 'sale') setSaleDiscount(discount);
    else setLines((l) => l.map((x) => (x.id === target.id ? { ...x, discount } : x)));
    setDiscountTarget(null);
  };

  /**
   * Open the drawer - change for a note, a float, a mistake. Needs the
   * permission or a manager, and is always logged. Through the print helper it
   * just opens; without it, a small "no sale" slip makes the printer open it.
   */
  const openDrawer = async () => {
    const ok = await approval.request(['till.drawer.open'], 'drawer_open', t('approval.reasonDrawer'));
    if (!ok) return;
    void api('/sales/drawer/open', { body: { reason: 'no_sale', approvedBy: ok.managerId } }).catch(() => undefined);
    const r = printRoute();
    if (r.viaHelper && r.receiptPrinter) {
      try {
        await openDrawerViaHelper();
      } catch (e) {
        toast.err(helperProblem(e instanceof HelperError ? e.code : String(e), t));
      }
      return;
    }
    void printNoSale(settings, user?.name ?? '');
  };

  const hold = async () => {
    if (lines.length === 0) return;
    try {
      await api('/sales/held', {
        body: {
          label: `${lines.length} ${t('common.items')} · ${money(totals.total)}`,
          payload: {
            lines: lines.map((l) => ({
              variantId: l.item.variantId,
              qty: l.qty,
              discount: l.discount,
              item: l.item,
            })),
            saleDiscount,
          },
        },
      });
      toast.ok(t('checkout.held'));
      reset();
    } catch {
      toast.err(t('net.needOnline'));
    }
  };

  const complete = async (payments: TakenPayment[], change: number, rounding: number) => {
    setPaying(false);

    /*
     * If this shop counts the drawer, a sale belongs to a shift - but counting
     * must never stand between a customer and paying, so a missing shift is
     * opened here with an unknown float and can be counted properly later.
     * Shops that have shifts switched off simply file the sale without one.
     */
    let shiftId = shift?.id ?? null;
    if (!shiftId && settings.shiftsEnabled) {
      try {
        const opened = await api<{ id: string }>('/sales/shifts/open', { body: { openingCash: {} } });
        shiftId = opened.id;
        setShift({ id: opened.id });
        localStorage.setItem(shopKey('pos.shift'), JSON.stringify({ id: opened.id }));
      } catch {
        // Offline, or not allowed to open one. The sale still goes through.
      }
    }

    const { receiptNo, counter } = takeReceiptNumber();
    const saleId = newId();

    const payload = {
      id: saleId,
      receiptNo,
      kind: 'sale' as const,
      deviceId: deviceId(),
      deviceCounter: counter,
      userId: user!.id,
      shiftId,
      customerId: customer?.id ?? null,
      createdAt: new Date().toISOString(),
      vatRate: settings.vatRate,
      saleDiscount,
      approvals,
      approvalToken,
      lines: lines.map((l) => ({
        id: l.id,
        variantId: l.item.variantId,
        qty: l.qty,
        discount: l.discount,
        promoId: promoHits.get(l.id)?.promoId ?? null,
        // Snapshots, used only if the item is later deleted from the catalogue.
        nameEn: l.item.nameEn,
        nameAr: l.item.nameAr,
        size: l.item.size,
        colorEn: l.item.colorEn,
        colorAr: l.item.colorAr,
        sku: l.item.sku,
        unitPrice: l.item.price,
        unitCost: l.item.avgCost,
      })),
      payments: payments.map((p) => ({
        id: p.id,
        method: p.method,
        currency: p.currency,
        amount: p.amount,
        rateMicros: p.rateMicros,
        decimals: p.decimals,
        reference: p.reference ?? null,
      })),
    };

    // Saved on this device first, then sent. A power cut here loses nothing.
    const res = await submitSale(saleId, payload);
    const tookCash = payments.some((p) => p.method === 'cash');

    // Take the sold items off the cached stock so the next scan is honest.
    await Promise.all(
      lines.map((l) =>
        db.items.update(l.item.variantId, { stock: Math.max(0, l.item.stock - l.qty) }).catch(() => undefined),
      ),
    );

    // Keep the receipt on this device too, so it can be exchanged immediately -
    // including during an outage, when the server has not seen it yet.
    await cacheCompletedSale(
      {
        id: saleId,
        receipt_no: receiptNo,
        kind: 'sale',
        status: 'completed',
        total: totals.total,
        created_at: payload.createdAt,
        vat_rate: settings.vatRate,
        user_id: user!.id,
      },
      totals.lines.map((calc) => {
        const l = lines.find((x) => x.id === calc.id)!;
        return {
          id: calc.id,
          sale_id: saleId,
          variant_id: calc.variantId,
          name_en: l.item.nameEn,
          name_ar: l.item.nameAr,
          size: l.item.size,
          color_en: l.item.colorEn,
          color_ar: l.item.colorAr,
          qty: calc.qty,
          unit_price: calc.unitPrice,
          net: calc.net,
          net_unit: calc.netUnit,
          returned_qty: 0,
          final_sale: l.item.finalSale ? 1 : 0,
        };
      }),
    ).catch(() => undefined);

    beepDone();
    const receipt: ReceiptData = {
        receiptNo,
        createdAt: payload.createdAt,
        cashierName: user!.name,
        customerName: customer?.name ?? null,
        lines: lines.map((l) => {
          const calc = totals.lines.find((x) => x.id === l.id)!;
          return {
            nameEn: l.item.nameEn,
            nameAr: l.item.nameAr,
            size: l.item.size,
            colorEn: l.item.colorEn,
            colorAr: l.item.colorAr,
            qty: l.qty,
            unitPrice: l.item.price,
            net: calc.net,
            promo: promoOn(l.id),
          };
        }),
        gross: totals.gross,
        discountTotal: totals.discountTotal,
        promoTotal: totals.promoTotal,
        total: totals.total,
        rounding,
        payable: totals.total + rounding,
        vatAmount: totals.vat.vat,
        netAmount: totals.vat.net,
        vatRate: settings.vatRate,
        changeIls: change,
        payments: payments.map((p) => ({
          // The method, not a label: the receipt names it in the SHOP's language.
          method: p.method,
          methodLabel: p.methodLabel,
          currency: p.currency,
          amount: p.amount,
          amountIls: Math.round((p.amount * p.rateMicros * 100) / (10 ** p.decimals * 1_000_000)),
          rate: p.rateMicros / 1_000_000,
        })),
    };
    setDone({ sent: res.sent, receipt });

    // The receipt by itself, if the shop asked for that - and the drawer with it
    // when cash changed hands, in the same job so both happen at once.
    const kick = tookCash && settings.drawerEnabled && settings.drawerOnCash;
    if (settings.autoPrintReceipt) void printReceipt(receipt, settings, false, { kick });
    else if (kick && printRoute().viaHelper && printRoute().receiptPrinter) void openDrawerViaHelper().catch(notifyPrintProblem);
    reset();
  };

  // --- render ---------------------------------------------------------------

  return (
    <div className="till">
      {/* The shop: search or scan at the top, everything tappable below. */}
      <div className="till-left">
        <div className="till-search">
          <div style={{ flex: 1, position: 'relative' }}>
            <input
              ref={searchRef}
              type="search"
              data-scan-target="true"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('checkout.searchOrScan')} aria-label={t('checkout.searchOrScan')}
              onKeyDown={(e) => {
                if (results.length === 0) return;
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setHighlight((h) => Math.min(h + 1, results.length - 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setHighlight((h) => Math.max(h - 1, 0));
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                  addItem(results[highlight]);
                } else if (e.key === 'Escape') {
                  setQuery('');
                  setResults([]);
                }
              }}
            />
            {results.length > 0 ? (
              <div className="search-results">
                {results.map((r, i) => (
                  <button key={r.variantId} className={i === highlight ? 'on' : ''} onClick={() => addItem(r)}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="nm">{lang === 'ar' ? r.nameAr || r.nameEn : r.nameEn || r.nameAr}</div>
                      <div className="meta">
                        {r.size} · <Swatch hex={r.colorHex} />{' '}
                        {lang === 'ar' ? r.colorAr || r.colorEn : r.colorEn || r.colorAr}
                      </div>
                    </div>
                    <div className="right">
                      <div className="strong tnum">{money(r.price)}</div>
                      <div className="meta tnum">{r.stock}</div>
                    </div>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <CameraButton onScan={(c) => void handleScan(c)} />
        </div>

        <div className="till-browse">
          <ProductGrid onPick={(item) => addItem(item)} />
        </div>
      </div>

      {/* The sale. On a phone it is not there at all until there is one. */}
      <div className={`till-right ${lines.length === 0 ? 'empty' : ''} ${cartOpen ? 'open' : ''}`}>
        {/* Phone only: the sale folded into one bar, so the shop stays on screen. */}
        <div className="till-bar">
          <button type="button" className="toggle" onClick={() => setCartOpen((v) => !v)} aria-expanded={cartOpen}>
            <span className="strong">
              {plural(totals.itemCount, {
                one: 'checkout.itemsOne',
                two: 'checkout.itemsTwo',
                few: 'checkout.itemsCount',
                many: 'checkout.itemsMany',
              })}
              {' · '}
              <span className="tnum">{money(totals.total)}</span>
            </span>
            <span className="xsmall muted">{cartOpen ? t('checkout.hideSale') : t('checkout.showSale')}</span>
          </button>
          <Button variant="primary" onClick={() => setPaying(true)}>
            {t('checkout.takePayment')}
          </Button>
        </div>
        <div className="till-lines">
          {lines.length === 0 ? (
            <div className="till-empty">
              <div className="big" aria-hidden>🛒</div>
              <div className="strong">{t('checkout.emptyTitle')}</div>
              <div className="small muted">{t('checkout.emptyHint')}</div>
            </div>
          ) : (
            lines.map((l) => {
              const calc = totals.lines.find((x) => x.id === l.id);
              const discounted = (calc?.promoDiscount ?? 0) + (calc?.lineDiscount ?? 0) + (calc?.saleDiscountShare ?? 0) > 0;
              const promo = promoOn(l.id);
              return (
                <div
                  key={l.id}
                  className={`line-row ${Date.now() - l.addedAt < 800 ? 'added' : ''}`}
                  onClick={() => setEditing(l)}
                >
                  <div style={{ minWidth: 0 }}>
                    <div className="nm">
                      {lang === 'ar' ? l.item.nameAr || l.item.nameEn : l.item.nameEn || l.item.nameAr}
                    </div>
                    <div className="meta">
                      <span>{l.item.size}</span>
                      <Swatch hex={l.item.colorHex} />
                      <span className="tnum">{money(l.item.price)}</span>
                      {l.qty > l.item.stock ? <Pill kind="danger">{t('stock.outOfStock')}</Pill> : null}
                    </div>
                    {promo ? (
                      <div className="line-promo">
                        <span aria-hidden>🏷</span> <span className="tnum">−{money(promo.amount)}</span>{' · '}
                        {lang === 'ar' ? promo.nameAr || promo.nameEn : promo.nameEn || promo.nameAr}
                      </div>
                    ) : null}
                  </div>

                  <div className="qty" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => setQty(l.id, l.qty - 1)} aria-label="-">−</button>
                    <span className="n">{l.qty}</span>
                    <button onClick={() => setQty(l.id, l.qty + 1)} aria-label="+">+</button>
                  </div>

                  <div className="money">
                    {discounted ? <div className="was tnum">{money(l.item.price * l.qty)}</div> : null}
                    <div className="tnum">{money(calc?.net ?? 0)}</div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="till-foot">
          <div className="till-total">
            <span>{t('common.total')}</span>
            <span className="v tnum">{money(totals.total)}</span>
          </div>
          {totals.promoTotal > 0 ? (
            <div className="till-sub" style={{ color: 'var(--ok)' }}>
              <span>{t('promo.onSale')}</span>
              <span className="tnum">−{money(totals.promoTotal)}</span>
            </div>
          ) : null}
          {totals.discountTotal - totals.promoTotal > 0 ? (
            <div className="till-sub" style={{ color: 'var(--ok)' }}>
              <span>{t('common.discount')}</span>
              <span className="tnum">−{money(totals.discountTotal - totals.promoTotal)}</span>
            </div>
          ) : null}
          <div className="till-sub muted">
            <span>
              {plural(totals.itemCount, {
                one: 'checkout.itemsOne',
                two: 'checkout.itemsTwo',
                few: 'checkout.itemsCount',
                many: 'checkout.itemsMany',
              })}
            </span>
            <span className="tnum">
              {t('common.vat')} {money(totals.vat.vat)}
            </span>
          </div>

          <Button
            variant="primary"
            size="lg"
            block
            disabled={lines.length === 0}
            onClick={() => setPaying(true)}
            style={{ marginBlockStart: 'var(--s-3)' }}
          >
            {t('checkout.takePayment')}
          </Button>

          {settings.drawerEnabled ? (
            <Button block onClick={() => void openDrawer()} style={{ marginBlockStart: 'var(--s-2)' }}>
              {t('print.openDrawer')}
            </Button>
          ) : null}

          {/* Everything else is one tap away, and out of the way until then. */}
          <div className="till-more">
            {/* Only for someone who can look customers up - which also means only
                in a shop that has the Customers module. */}
            {can('customer.view') ? (
              <button onClick={() => setPickingCustomer(true)}>
                {customer ? `👤 ${customer.name}` : t('checkout.addCustomer')}
              </button>
            ) : null}
            <button disabled={lines.length === 0} onClick={() => setDiscountTarget('sale')}>
              {t('checkout.saleDiscount')}
            </button>
            {settings.holdSalesEnabled && can('till.hold') ? (
              <button disabled={lines.length === 0} onClick={() => void hold()}>
                {t('checkout.hold')}
              </button>
            ) : null}
            <button disabled={lines.length === 0} onClick={() => setConfirmCancel(true)}>
              {t('checkout.cancelSale')}
            </button>
          </div>
          {/* Only where there is a keyboard: a touch till has no F-keys. */}
          <div className="till-keys" aria-hidden>{t('checkout.shortcuts')}</div>
        </div>
      </div>

      {/* --- dialogs --------------------------------------------------------- */}

      {editing ? (
        <LineEditor
          line={editing}
          onClose={() => setEditing(null)}
          onRemove={() => {
            setLines((l) => l.filter((x) => x.id !== editing.id));
            setEditing(null);
          }}
          onDiscount={() => {
            setDiscountTarget(editing);
            setEditing(null);
          }}
          onSwap={(item) => {
            setLines((l) => l.map((x) => (x.id === editing.id ? { ...x, item } : x)));
            setEditing(null);
          }}
        />
      ) : null}

      {discountTarget ? (
        <DiscountDialog
          base={
            discountTarget === 'sale' ? saleDiscountBase(totals) : lineBase(discountTarget)
          }
          current={discountTarget === 'sale' ? saleDiscount : discountTarget.discount}
          onApply={(d) => void applyDiscount(discountTarget, d)}
          onClose={() => setDiscountTarget(null)}
        />
      ) : null}

      {paying ? (
        <PaymentDialog
          total={totals.total}
          customer={customer}
          allowTab={settings.customerTabsEnabled && can('customer.tab')}
          allowGift={hasFeature('giftcards')}
          allowCredit={hasFeature('exchanges')}
          onNeedCustomer={() => {
            setPaying(false);
            setPayAfterNaming(true);
            setPickingCustomer(true);
          }}
          onDone={(p, change, rounding) => void complete(p, change, rounding)}
          onCancel={() => setPaying(false)}
        />
      ) : null}

      {pricing ? (
        <PriceAsk
          item={pricing.item}
          qty={pricing.qty}
          onCancel={() => setPricing(null)}
          onDone={(price) => addPriced(pricing.item, pricing.qty, price)}
        />
      ) : null}

      {unknown ? (
        <Modal
          title={t('checkout.unknownBarcode', { code: unknown })}
          onClose={() => setUnknown(null)}
          footer={
            <>
              <Button onClick={() => setUnknown(null)}>{t('common.close')}</Button>
              <div className="spacer" />
              <Button
                variant="primary"
                onClick={() => {
                  setQuery(unknown);
                  setUnknown(null);
                  searchRef.current?.focus();
                }}
              >
                {t('checkout.searchInstead')}
              </Button>
            </>
          }
        >
          <p className="small muted">{t('checkout.scanHint')}</p>
        </Modal>
      ) : null}

      {confirmCancel ? (
        <Confirm
          title={t('checkout.cancelSale')}
          message={t('checkout.cancelConfirm')}
          confirmLabel={t('checkout.cancelSale')}
          danger
          onConfirm={() => {
            reset();
            setConfirmCancel(false);
          }}
          onCancel={() => setConfirmCancel(false)}
        />
      ) : null}

      {pickingCustomer ? (
        <CustomerPicker
          current={customer}
          onPick={(c) => {
            setCustomer(c);
            setPickingCustomer(false);
            if (payAfterNaming) {
              setPayAfterNaming(false);
              setPaying(true);
            }
          }}
          onClose={() => {
            setPickingCustomer(false);
            setPayAfterNaming(false);
          }}
        />
      ) : null}

      {done ? <SaleDone done={done} onClose={() => setDone(null)} /> : null}

      {approval.element}
    </div>
  );
}

// ---------------------------------------------------------------------------

/** Find a customer by name or phone, or add one without leaving the till. */
function CustomerPicker({
  current,
  onPick,
  onClose,
}: {
  current: { id: string; name: string; phone?: string } | null;
  onPick: (c: { id: string; name: string; phone?: string } | null) => void;
  onClose: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<{ id: string; name: string; phone?: string }[]>([]);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (q.trim().length < 2) {
        setRows([]);
        return;
      }
      try {
        setRows(await api(`/exchange/customers?q=${encodeURIComponent(q)}`));
      } catch {
        // Offline: a customer cannot be looked up, so the sale stays unnamed.
        toast.warn(t('net.needOnline'));
      }
    }, 220);
    return () => clearTimeout(timer);
  }, [q, t, toast]);

  const create = async () => {
    setBusy(true);
    try {
      const made = await api<{ id: string; name: string; phone?: string }>('/exchange/customers', {
        body: { name: name.trim(), phone: phone.trim() || null },
      });
      onPick(made);
    } catch (e) {
      toast.err((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={t('common.customer')}
      onClose={onClose}
      footer={
        <>
          {current ? (
            <Button variant="ghost" onClick={() => onPick(null)}>
              {t('checkout.noCustomer')}
            </Button>
          ) : null}
          <div className="spacer" />
          <Button onClick={onClose}>{t('common.cancel')}</Button>
        </>
      }
    >
      {adding ? (
        <div className="col">
          <Field label={t('common.name')}>
            <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </Field>
          <Field label={t('common.phone')}>
            <input type="tel" dir="ltr" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </Field>
          <div className="btn-row">
            <Button onClick={() => setAdding(false)}>{t('common.back')}</Button>
            <div className="spacer" />
            <Button variant="primary" busy={busy} disabled={!name.trim()} onClick={() => void create()}>
              {t('common.save')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="col">
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('sales.searchPlaceholder')} aria-label={t('sales.searchPlaceholder')}
            autoFocus
          />
          {rows.map((c) => (
            <button key={c.id} className="pick-row" onClick={() => onPick(c)}>
              <span className="grow strong">{c.name}</span>
              <span className="xsmall muted tnum">{c.phone ?? ''}</span>
            </button>
          ))}
          <Button
            onClick={() => {
              setName(q);
              setAdding(true);
            }}
          >
            {t('customers.new')}
          </Button>
        </div>
      )}
    </Modal>
  );
}

function LineEditor({
  line,
  onClose,
  onRemove,
  onDiscount,
  onSwap,
}: {
  line: Line;
  onClose: () => void;
  onRemove: () => void;
  onDiscount: () => void;
  onSwap: (item: CatalogItem) => void;
}) {
  const t = useT();
  const { lang } = useI18n();
  const { money } = useSettings();
  const [siblings, setSiblings] = useState<CatalogItem[]>([]);

  /** The shop's two languages, the way the rest of the till does it. */
  const nm = (x: { nameEn: string; nameAr: string }) =>
    lang === 'ar' ? x.nameAr || x.nameEn : x.nameEn || x.nameAr;
  const col = (x: { colorEn: string; colorAr: string }) =>
    lang === 'ar' ? x.colorAr || x.colorEn : x.colorEn || x.colorAr;

  useEffect(() => {
    void db.items
      .where('productId')
      .equals(line.item.productId)
      .toArray()
      // Sizes sorted as words read "L, M, S, XL, XS" - useless when hunting for
      // the next size up mid-sale. compareSizes puts them in wearing order.
      .then((rows) =>
        setSiblings(
          rows.sort((a, b) => compareSizes(a.size, b.size) || a.colorEn.localeCompare(b.colorEn)),
        ),
      );
  }, [line.item.productId]);

  return (
    <Modal title={nm(line.item)} onClose={onClose}>
      <div className="col">
        <div className="row">
          <Pill>{line.item.size}</Pill>
          <Pill>
            <Swatch hex={line.item.colorHex} /> {col(line.item)}
          </Pill>
          <div className="spacer" />
          <strong className="tnum">{money(line.item.price)}</strong>
        </div>

        <h3>{t('checkout.changeSize')}</h3>
        <div className="btn-row">
          {siblings.map((s) => (
            <Button
              key={s.variantId}
              size="sm"
              variant={s.variantId === line.item.variantId ? 'primary' : 'default'}
              disabled={s.stock <= 0 && s.variantId !== line.item.variantId}
              onClick={() => onSwap(s)}
            >
              {s.size} {col(s)}
              <span className="xsmall muted"> ({s.stock})</span>
            </Button>
          ))}
        </div>

        <div className="btn-row" style={{ marginBlockStart: 'var(--s-4)' }}>
          <Button onClick={onDiscount}>{t('checkout.lineDiscount')}</Button>
          <div className="spacer" />
          <Button variant="danger" onClick={onRemove}>
            {t('checkout.removeLine')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function DiscountDialog({
  base,
  current,
  onApply,
  onClose,
}: {
  base: number;
  current: Discount;
  onApply: (d: Discount) => void;
  onClose: () => void;
}) {
  const t = useT();
  const { money } = useSettings();
  const [type, setType] = useState<'percent' | 'fixed'>(current.type === 'fixed' ? 'fixed' : 'percent');
  const [percent, setPercent] = useState(current.type === 'percent' ? current.value : 10);
  const [fixed, setFixed] = useState(current.type === 'fixed' ? current.value : 0);

  const value = type === 'percent' ? percent : fixed;
  const off = type === 'percent' ? Math.round((base * percent) / 100) : Math.min(fixed, base);

  return (
    <Modal
      title={t('common.discount')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={() => onApply(NO_DISCOUNT)}>{t('common.clear')}</Button>
          <div className="spacer" />
          <Button variant="primary" onClick={() => onApply({ type, value })} disabled={value <= 0}>
            {t('common.apply')}
          </Button>
        </>
      }
    >
      <div className="col">
        <div className="btn-row">
          <Button variant={type === 'percent' ? 'primary' : 'default'} onClick={() => setType('percent')}>
            {t('common.percent')}
          </Button>
          <Button variant={type === 'fixed' ? 'primary' : 'default'} onClick={() => setType('fixed')}>
            {t('common.fixed')}
          </Button>
        </div>

        {type === 'percent' ? (
          <>
            <div className="btn-row">
              {[5, 10, 15, 20, 25, 50].map((p) => (
                <Button key={p} size="sm" variant={percent === p ? 'primary' : 'default'} onClick={() => setPercent(p)}>
                  {p}%
                </Button>
              ))}
            </div>
            <Field label="%">
              <input
                type="number"
                min={0}
                max={100}
                value={percent}
                onChange={(e) => setPercent(Number(e.target.value))}
                className="big tnum"
              />
            </Field>
          </>
        ) : (
          <Field label={t('common.amount')}>
            <MoneyInput className="big" value={fixed} onValue={setFixed} autoFocus />
          </Field>
        )}

        <div className="row">
          <span className="muted">{t('common.discount')}</span>
          <div className="spacer" />
          <strong className="tnum">−{money(off)}</strong>
        </div>
        <div className="row">
          <span className="muted">{t('common.total')}</span>
          <div className="spacer" />
          <strong className="tnum">{money(base - off)}</strong>
        </div>
      </div>
    </Modal>
  );
}

function SaleDone({ done, onClose }: { done: { receipt: ReceiptData; sent: boolean }; onClose: () => void }) {
  const t = useT();
  const { settings, money } = useSettings();

  return (
    <Modal
      title={t('checkout.completed')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('receipt.skip')}</Button>
          <div className="spacer" />
          <Button onClick={() => void printReceipt(done.receipt, settings, true)}>{t('receipt.gift')}</Button>
          <Button variant="primary" onClick={() => void printReceipt(done.receipt, settings)}>
            {t('receipt.print')}
          </Button>
        </>
      }
    >
      <div className="col center" style={{ alignItems: 'center', gap: 'var(--s-3)' }}>
        <div style={{ fontSize: '2.5rem' }} aria-hidden>✓</div>
        <div className="strong" style={{ fontSize: 'var(--t-2xl)' }}>
          <Money value={done.receipt.payable} />
        </div>
        {done.receipt.changeIls > 0 ? (
          <div className="row">
            <Pill kind="accent">
              {t('pay.changeDue')} {money(done.receipt.changeIls)}
            </Pill>
          </div>
        ) : null}
        <div className="small muted">{t('receipt.no')} {done.receipt.receiptNo}</div>
        {!done.sent ? <Pill kind="warn">{t('net.offlineNote')}</Pill> : null}
      </div>
    </Modal>
  );
}

/**
 * What is this one worth?
 *
 * Asked for an item the shop prices at the counter. It opens on the number pad
 * with nothing in it, because there is no sensible number to suggest - the last
 * price this item sold for is the price of something else entirely.
 */
function PriceAsk({
  item,
  qty,
  onDone,
  onCancel,
}: {
  item: CatalogItem;
  qty: number;
  onDone: (price: number) => void;
  onCancel: () => void;
}) {
  const t = useT();
  const { lang } = useI18n();
  const { money } = useSettings();
  const [price, setPrice] = useState(0);
  const name = lang === 'ar' ? item.nameAr || item.nameEn : item.nameEn || item.nameAr;

  return (
    <Modal
      title={name}
      onClose={onCancel}
      footer={
        <>
          <Button onClick={onCancel}>{t('common.cancel')}</Button>
          <div className="spacer" />
          {/* Nothing to put on the sale until there is a price on it. */}
          <Button variant="primary" disabled={price <= 0} onClick={() => onDone(price)}>
            {t('common.add')}
          </Button>
        </>
      }
    >
      <div className="col">
        <Field label={t('checkout.priceEach')}>
          <MoneyInput
            className="big"
            value={price}
            onValue={setPrice}
            decimals={2}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && price > 0) onDone(price);
            }}
          />
        </Field>
        {qty > 1 ? (
          <p className="small muted">
            {t('checkout.priceForEach', { n: qty, total: money(price * qty) })}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
